import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { FormInput } from '@dx-ui/osc-form';
import { Icon } from '@dx-ui/osc-icon';
import { Button, Link, Text } from '@dx-ui/global-ui-prism';
import { TwoFactorAuthError, TwoFactorForm } from '@dx-ui/osc-two-factor-auth';
import type { FormData } from '../hooks/useLogin';
import useLogin from '../hooks/useLogin';
import type {
  DeliveryMethod,
  LoginFormConfig,
  LoginFormResponse,
  LoginData,
  LoginError,
  SourceID,
} from './login-form.types';
import { LoginErrors, LoginStatus } from './login-form.types';
import { ANALYTICS_GLOBAL_CLICK_EVENT } from '@dx-ui/config-metrics';
import {
  trackLoginClick,
  FORGOT_INFO_LINK,
  SECURE_INFO_LINK,
  trackLoginError,
  buildUrl,
  JOIN_LINK,
  resolveSignInProfileStatus,
  sendUsernameLoginReward,
  SIGN_IN_CLICK_ID,
} from './login-form.utilities';
import HelpBlock from './help-block';
import useTwoFactor from '../hooks/useTwoFactor';
import { logError } from '@dx-ui/framework-logger';
import { BannerNotification } from '@dx-ui/osc-banner-notification';
import { ShowHidePasswordInput } from './show-hide-password-input';
import { generateError } from '@dx-ui/utilities-generate-error';
import { isBrowser } from '@dx-ui/utilities-is-browser';
import { FormProvider, SubmitErrorHandler, useForm } from 'react-hook-form';
import { useAkamaiResubmission } from '../hooks/useAkamaiResubmission';
import { sendReward } from '@dx-ui/framework-conductrics';
import { useForgotInfoLinkCopyTest } from '../hooks/use-forgot-info-link-copy-test';
import {
  dispatchSignInInit,
  dispatchSignInModalView,
  dispatchSignInPageStopError,
  dispatchSignInSuccess,
  dispatchSignInValidationError,
} from './login-form-analytics';
import type { SigninAnalytics } from './login-form-analytics';

export type LoginFormProps = {
  /**
   * Callback to run when submitting the form
   */
  onLoginAttempt: (authResponse: LoginFormResponse) => void;
  /**
   * Transport-free semantic callbacks used by Sign In analytics.
   */
  analytics?: SigninAnalytics;
  /**
   * When true (default), inventoried legacy `trackLoginClick` / `trackLoginError`
   * paths run. Hosts pass false after Core mapping cutover. Callback presence
   * never selects or suppresses legacy.
   */
  isLegacySignInAnalyticsEnabled?: boolean;
  /**
   * Source of the login request. Either 'OHW' or 'groups360'
   */
  sourceId: SourceID;
  /**
   * Whether the login form is shown inside a modal.
   * Used to adjust styles and behavior accordingly.
   */
  isInModal?: boolean;
  /**
   * Configure the display of links on the form
   */
  loginFormConfig?: LoginFormConfig;
  /**
   * The username to prefill the form with
   */
  initialUsername?: string;
};

type FormState = 'login' | 'otp' | 'stopGap' | 'continue';

export const LoginForm = ({
  onLoginAttempt,
  analytics,
  isLegacySignInAnalyticsEnabled = true,
  sourceId = 'OHW',
  isInModal = false,
  loginFormConfig,
  initialUsername = '',
}: LoginFormProps) => {
  const { t } = useTranslation('osc-login');
  const {
    isForgotCopyLinkCopy,
    isForgotCopyLinkPlacement,
    isForgotCopyTestGoal,
    isForgotCopyTestLoaded,
  } = useForgotInfoLinkCopyTest();
  const language = useRouter().locale || 'en';
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const form = useForm<FormData>({
    mode: 'all',
    criteriaMode: 'all',
    defaultValues: {
      username: initialUsername,
      password: '',
    },
  });

  const { handleSubmit } = form;
  const [formState, setFormState] = useState<FormState>('login');
  const [formData, setFormData] = useState<FormData>({ username: '', password: '' });
  const [otpDeliveryMethod, setOTPDeliveryMethod] = useState<DeliveryMethod | null>(null);
  const { login, loading } = useLogin();
  const { submitOTPCode } = useTwoFactor();

  const config: LoginFormConfig = loginFormConfig || {
    showCreatePasswordLink: false,
    showForgetInfoLink: true,
    showJoinLink: true,
    showSecureInfoLink: false,
  };

  const errorBannerRef = useRef<HTMLParagraphElement>(null);
  const isSubmittingRef = useRef(false);
  const usernameRef: HTMLInputElement | null = isBrowser
    ? document.querySelector('input[name=username]')
    : null;

  const formRef = useAkamaiResubmission<HTMLFormElement>();

  useEffect(() => {
    if (errorMessage && errorBannerRef.current) {
      errorBannerRef.current.focus();
    }
  }, [errorMessage]);

  useEffect(() => {
    dispatchSignInModalView(analytics);
    // Report Sign In surface view once per mount; host owns mapping/enqueue.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- modal-view is a mount lifecycle event
  }, []);

  const handleError = (error: LoginError | null, formFieldsMissing?: string) => {
    if (error) {
      sendReward('g-login-error');
      const status = error.status;
      const alertMessage = t(`error.${status}`);
      setErrorMessage(alertMessage);

      if (isBrowser) {
        if (status === LoginErrors.INVALID_ENTRY && formFieldsMissing) {
          dispatchSignInValidationError({
            trackLegacy: () =>
              trackLoginError(
                status,
                ANALYTICS_GLOBAL_CLICK_EVENT,
                alertMessage,
                formFieldsMissing
              ),
            onSignInValidationError: analytics?.onSignInValidationError,
            payload: {
              formFields: formFieldsMissing,
              alertMessage,
            },
            isLegacySignInAnalyticsEnabled,
          });
        } else {
          dispatchSignInPageStopError({
            trackLegacy: () =>
              trackLoginError(
                status,
                ANALYTICS_GLOBAL_CLICK_EVENT,
                alertMessage,
                formFieldsMissing
              ),
            onSignInPageStopError: analytics?.onSignInPageStopError,
            payload: {
              errorCode: status,
              alertMessage,
            },
            isLegacySignInAnalyticsEnabled,
          });
        }

        form.reset();

        if (status === 'invalid_entry') {
          usernameRef?.focus();
        }
      }
    }
  };

  const handleLoginResult = (result: LoginFormResponse, form: FormData) => {
    const error: LoginError | undefined = result?.error;
    const safeError = error && Object.values(LoginErrors).includes(error?.status) ? error : null;

    switch (result?.status) {
      case LoginStatus.CONTINUE: {
        const loginData: LoginData = {
          hhonorsNumber: result.data?.userInfo?.hhonorsNumber,
          hhonorsTier: result.data?.userInfo?.hhonorsTier,
          hhonorsPackageCodes: result.data?.userInfo?.hhonorsPackageCodes,
        };

        dispatchSignInSuccess({
          trackLegacy: () => trackLoginClick(SIGN_IN_CLICK_ID.SUCCESS, loginData),
          onSignInSuccess: analytics?.onSignInSuccess,
          payload: {
            profileId:
              loginData.hhonorsNumber === undefined || loginData.hhonorsNumber === null
                ? undefined
                : String(loginData.hhonorsNumber),
            rewardsTier: loginData.hhonorsTier,
            userType: Array.isArray(loginData.hhonorsPackageCodes)
              ? loginData.hhonorsPackageCodes.join(',')
              : undefined,
            profileStatus: isBrowser
              ? resolveSignInProfileStatus(loginData.hhonorsPackageCodes)
              : undefined,
          },
          isLegacySignInAnalyticsEnabled,
        });
        sendReward('g-sign-in-after-forgot-info');
        sendUsernameLoginReward(form.username);
        onLoginAttempt(result);
        break;
      }
      case LoginStatus.CHALLENGE:
        setFormData(form);
        setOTPDeliveryMethod(result?.deliveryMethod || 'sms');
        if (formState === 'otp') {
          throw new TwoFactorAuthError('EXPIRED_OR_INVALID_CODE');
        } else {
          setFormState('otp');
        }
        break;
      case LoginStatus.STUCK_ON_LOGIN:
        dispatchSignInPageStopError({
          trackLegacy: () => trackLoginClick(SIGN_IN_CLICK_ID.PAGE_STOP),
          onSignInPageStopError: analytics?.onSignInPageStopError,
          payload: {
            errorCode: LoginErrors.STUCK_ON_LOGIN,
            alertMessage: t('error.sol', { defaultValue: '' }),
          },
          isLegacySignInAnalyticsEnabled,
        });
        setFormState('stopGap');
        break;
      default:
        setErrorMessage(t('error.unexpected_error'));
        handleError(safeError);
        if (formState === 'otp') {
          throw new TwoFactorAuthError('UNKNOWN_ERROR');
        }
        break;
    }
  };

  const onSubmit = async (formData: FormData) => {
    try {
      if (errorMessage) {
        setErrorMessage(undefined);
      }
      if (!isSubmittingRef.current) {
        dispatchSignInInit({
          trackLegacy: () => trackLoginClick(SIGN_IN_CLICK_ID.INIT),
          onSignInInit: analytics?.onSignInInit,
          isLegacySignInAnalyticsEnabled,
        });
        isSubmittingRef.current = true;
      }

      const result = await login(formData, sourceId, loginFormConfig?.isPartner);
      const loginError: LoginError | undefined = result?.error;

      if (result) {
        handleLoginResult(result, formData);
      }

      if (loginError) {
        setErrorMessage(t(`error.${loginError.status}`, { defaultValue: '' }));
      }
    } catch (e) {
      logError('LOGIN_FORM', generateError(e));
      setErrorMessage(t('error.unexpected_error'));
    } finally {
      isSubmittingRef.current = false;
      analytics?.onSignInAttemptSettled?.();
    }
  };

  const onError: SubmitErrorHandler<FormData> = (errors) => {
    const { username, password } = errors;

    if (username || password) {
      const formFieldsMissing =
        username && password ? 'username|password' : username ? 'username' : 'password';
      handleError({ status: LoginErrors.INVALID_ENTRY }, formFieldsMissing);
      // Client validation never enters onSubmit; still settle after the outcome callback.
      analytics?.onSignInAttemptSettled?.();
    }
  };

  const onOTPSubmit = async (code: string) => {
    const result = await submitOTPCode(sourceId, code, formData, loginFormConfig?.isPartner);

    if (result) {
      handleLoginResult(result, formData);
    } else {
      handleError({ status: LoginErrors.UNKNOWN_ERROR });
      logError('LOGIN_FORM_OTP', 'UNKNOWN_ERROR', `response: ${JSON.stringify(result)}`);
      throw new TwoFactorAuthError('UNKNOWN_ERROR');
    }
  };

  return (
    <div>
      <BannerNotification status="error">
        {errorMessage ? (
          <p ref={errorBannerRef} tabIndex={-1} role="alert" className="font-bold">
            {errorMessage}
          </p>
        ) : null}
      </BannerNotification>
      {formState === 'login' ? (
        <FormProvider {...form}>
          <form
            className="space-y-4 pb-1"
            onSubmit={(e) => {
              e.stopPropagation();
              return handleSubmit(onSubmit, onError)(e);
            }}
            method="post"
            ref={formRef}
          >
            <Text id="fields-required">{t('fieldsRequired')}</Text>
            <FormInput
              labelClassName="w-full"
              name="username"
              label={t('username')}
              autoComplete="username"
              aria-label={t('usernameLabel')}
              aria-required={true}
              aria-describedby="fields-required"
              registerOptions={{
                required: t(`usernameRequired`),
              }}
            />
            <ShowHidePasswordInput
              name="password"
              label={t('password.label')}
              registerOptions={{
                required: t('password.requiredError'),
              }}
            />

            {config.showForgetInfoLink &&
            isForgotCopyTestLoaded &&
            isForgotCopyLinkPlacement === 'aboveSignIn' ? (
              <div className="flex items-center pt-2">
                <div className="ms-2">
                  <Link
                    data-e2e="forgotInfoLink"
                    isNewWindow={config.isNewWindow}
                    url={buildUrl(FORGOT_INFO_LINK, language)}
                    onClick={() => sendReward(isForgotCopyTestGoal)}
                  >
                    {isForgotCopyLinkCopy}
                  </Link>
                </div>
              </div>
            ) : null}

            <Button
              fill="solid"
              label={t('signIn')}
              type="submit"
              disabled={loading}
              data-e2e="signInButton"
              className={isInModal ? 'w-full' : undefined}
            />

            {config.showForgetInfoLink &&
            isForgotCopyTestLoaded &&
            isForgotCopyLinkPlacement === 'belowSignIn' ? (
              <div className="flex items-center pt-2">
                <div className="ms-2">
                  <Link
                    data-e2e="forgotInfoLink"
                    isNewWindow={config.isNewWindow}
                    url={buildUrl(FORGOT_INFO_LINK, language)}
                    onClick={() => sendReward(isForgotCopyTestGoal)}
                  >
                    {isForgotCopyLinkCopy}
                  </Link>
                </div>
              </div>
            ) : null}

            {config.showCreatePasswordLink && config.showForgetInfoLink ? (
              <div className="flex items-center pt-2">
                <Text as="span">{t('firstTime')}</Text>
                &nbsp;
                <Link
                  url={buildUrl(FORGOT_INFO_LINK, language)}
                  data-e2e="createPasswordLink"
                  isNewWindow={config.isNewWindow}
                >
                  {t('createPassword')}
                </Link>
              </div>
            ) : null}

            {!config.showJoinLink && config.showSecureInfoLink ? (
              <div className="flex items-center gap-1 pt-2">
                <Link
                  data-e2e="yourInfoLink"
                  isNewWindow={config.isNewWindow}
                  url={buildUrl(SECURE_INFO_LINK, language)}
                >
                  {t('secureInformation')}
                </Link>
                <Icon name="lock" />
              </div>
            ) : null}
          </form>
        </FormProvider>
      ) : null}
      {formState === 'login' && config.showJoinLink ? (
        <div className="mt-4 flex-wrap justify-center bg-surface-legacy pt-2">
          <Text className="text-center">{t('joinMessage')}</Text>
          <div className="my-2 text-center">
            {config.joinFromModal ? (
              <Button
                fill="outline"
                label={t('joinNow')}
                onClick={() => {
                  sendReward('g-login-form-join-click');
                  config.onJoinClick?.();
                }}
                className="md:self-start"
              />
            ) : (
              <Button
                as="Link"
                fill="outline"
                label={t('joinNow')}
                className="md:self-start"
                data-e2e="signInButton"
                isNewWindow={config.isNewWindow}
                underline={false}
                url={buildUrl(JOIN_LINK, language, config.joinOcode, config.forwardPageURL)}
                onClick={() => {
                  sendReward('g-login-form-join-click');
                }}
              />
            )}
          </div>
          <div className="my-2 flex items-center justify-center gap-1 text-center">
            <Link
              data-e2e="yourInfoLink"
              isNewWindow={config.isNewWindow}
              url={buildUrl(SECURE_INFO_LINK, language)}
            >
              {t('secureInformation')}
            </Link>
            <Icon name="lock" />
          </div>
        </div>
      ) : null}
      {formState === 'otp' ? (
        <TwoFactorForm
          subHeadlineOrDescription={
            otpDeliveryMethod === 'email'
              ? t('codeSentDescriptionEmail')
              : t('codeSentDescriptionPhone')
          }
          deliveryMethod={otpDeliveryMethod || ''}
          onSubmit={onOTPSubmit}
          language="en"
          shouldUseAuthApiResend={true}
        />
      ) : null}
      {formState === 'stopGap' && <HelpBlock />}
    </div>
  );
};
